import { useCallback, useRef, useState, type RefCallback } from 'react'

/**
 * Track an element's width so SVG charts can lay out in real pixels.
 *
 * A **callback ref**, not `useRef` + `useEffect`, and it measures synchronously
 * the moment the node attaches. Both details fix the same bug: a chart that
 * rendered severely compressed on first open and only corrected on a manual
 * refresh.
 *
 * The old version started at `0` and updated only when a `ResizeObserver` fired
 * *after* paint, so the first painted frame used the caller's fallback width
 * (`MonthRateChart` clamps to 320px) — a chart squeezed into the left of its
 * panel until the async callback arrived. A callback ref runs during the commit
 * phase, before the browser paints, so reading `getBoundingClientRect().width`
 * there makes the *first* frame correct. It also re-runs whenever the observed
 * node changes, so a chart that mounts after its data arrives (an early return
 * before the ref'd element exists) is still measured — a plain `useEffect([])`
 * would have attached the observer to nothing and never re-run.
 *
 * The observer stays, for genuine resizes (window, panel, the theme toggle
 * reflowing). The synchronous read is the initial value; the observer keeps it
 * live.
 */
export function useElementWidth<T extends HTMLElement>(): { ref: RefCallback<T>; width: number } {
  const [width, setWidth] = useState(0)
  const observer = useRef<ResizeObserver | null>(null)
  const frame = useRef(0)

  const ref = useCallback<RefCallback<T>>((element) => {
    // Detach from any previous node before observing a new one.
    observer.current?.disconnect()
    observer.current = null
    if (frame.current !== 0 && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(frame.current)
      frame.current = 0
    }
    if (element === null) return

    // Synchronous initial measurement, before paint — this is what makes the
    // first frame correct on a full page load, where the layout is already
    // settled when the node attaches. `.chart-wrap` has no padding, so the
    // border-box width here equals the content-box width the observer reports.
    setWidth(element.getBoundingClientRect().width)

    // Re-measure on the next frame. On a *client-side navigation* into a report
    // (the home grid → department → report path), the left rail is inserted in
    // the same commit that mounts the chart, so the synchronous read above can
    // land mid-reflow and capture a width that never triggers a later resize
    // event — leaving the chart wrong until a manual page refresh. A single rAF
    // re-read happens after the browser has settled the layout, correcting it
    // with no refresh. Harmless when the first read was already right (it sets
    // the same value). Guarded for the non-DOM test/SSR environment.
    if (typeof requestAnimationFrame !== 'undefined') {
      frame.current = requestAnimationFrame(() => {
        frame.current = 0
        setWidth(element.getBoundingClientRect().width)
      })
    }

    observer.current = new ResizeObserver(([entry]) => {
      if (entry !== undefined) setWidth(entry.contentRect.width)
    })
    observer.current.observe(element)
  }, [])

  return { ref, width }
}
